import React, { useCallback, useEffect, useReducer } from "react";
import { AuthAction, AuthActionType, AuthState } from "./CognitoOTPTypes";
import { useHistory, useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { clearNotificationsToken } from "core/services/api";
import allActions from "core/store/actions";
import { signIn, getCurrentUser, signOut, fetchAuthSession, fetchUserAttributes, confirmSignIn } from 'aws-amplify/auth';
import { CognitoOTPAuthContext, CognitoOTPAuthContextInitialState } from "./CognitoOTPAuthContext";
import { isOpenRoute } from "core/utils/route";

const CognitoOTPAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const handlers: Record<AuthActionType, (state: AuthState, action: AuthAction) => AuthState> = {
    AUTHENTICATE: (state, action) => {
      const { isAuthenticated, user } = action.payload || { isAuthenticated: false, user: null };
      return {
        ...state,
        isAuthenticated,
        isInitialized: true,
        user,
      };
    },
    LOGOUT: (state) => ({
      ...state,
      isAuthenticated: false,
      user: null,
    }),
  };

  const reducer = (state: AuthState, action: AuthAction): AuthState => {
    const handler = handlers[action.type];
    return handler ? handler(state, action) : state;
  };

  const [state, dispatch] = useReducer(reducer, CognitoOTPAuthContextInitialState);

  const globalDispatch = useDispatch()

  const history = useHistory()

  const location: any = useLocation()
  const fcmToken: any = location?.state?.notificationToken

  const userDetail = useSelector((state: any) => state?.user?.userDetail)

  const getSession = useCallback(
    () =>
      new Promise(async (resolve, reject) => {
        try {
          const session = await fetchAuthSession();
          const accessToken = session?.tokens?.accessToken.toString();
          const idToken = session?.tokens?.idToken?.toString();

          if (accessToken && idToken) {
            // Store tokens in session storage
            sessionStorage.setItem('accessToken', accessToken);
            sessionStorage.setItem('idToken', idToken);

            const user = await getCurrentUser();
            const attributes = await fetchUserAttributes();
            const token = session?.tokens?.idToken?.toString();
            dispatch({
              type: 'AUTHENTICATE',
              payload: { isAuthenticated: true, user: attributes },
            });
            resolve({
              user,
              session,
              headers: { Authorization: token },
            });
          } else {
            dispatch({
              type: 'AUTHENTICATE',
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });
            logout();
            resolve(null);
          }
        } catch (error) {
          console.error("Cognito get session error", error)
          reject(error);
        }
      }),
    []
  );

  const initial = useCallback(async () => {
    const accessToken = sessionStorage.getItem("accessToken");
    const idToken = sessionStorage.getItem("idToken");

    if (accessToken && idToken) {
      try {
        await getSession();
      } catch (error) {
        dispatch({
          type: 'AUTHENTICATE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
        logout();
      }
    } else {
      dispatch({
        type: 'AUTHENTICATE',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
      logout();
    }
  }, [getSession]);

  const initiateLogin = useCallback((userName: string) =>
    new Promise(async (resolve, reject) => {
      try {
        const signin = await signIn({
          username: userName,
          options: {
            authFlowType: 'USER_AUTH',
            preferredChallenge: 'SMS_OTP',
          }
        })
        resolve(signin);
      } catch (err) {
        reject(err)
      }
    }), [getSession])

  const login = useCallback(
    (otp: string) =>
      new Promise(async (resolve, reject) => {
        try {
          await confirmSignIn({
            challengeResponse: otp,
          });

          const authSession = await getSession();
          resolve(authSession);
        } catch (err) {
          reject(err)
        }
      }),
    [getSession]
  );

  const logout = async (revokeToken: boolean = true) => {
    try {
      const user = await getCurrentUser();
      if (user) {
        const payload: any = {}
        payload["token"] = fcmToken
        if (userDetail?.roles?.includes("PATIENT")) {
          payload["patientId"] = userDetail?.id
          payload["userType"] = "PATIENT"
        } else {
          payload["staffId"] = userDetail?.id
          payload["userType"] = "staff"
        }

        await clearNotificationsToken(payload)
      }
    } catch (err: any) {
      if (err.name !== "UserUnAuthenticatedException")
        console.error(err)
    } finally {
      dispatch({ type: 'LOGOUT' });

      if (revokeToken) {
        await signOut();
      }

      // Clear redux states
      allActions.map((action) => { globalDispatch(action.reset()) })

      // Clear local storage and session storage
      localStorage.clear();
      sessionStorage.clear();

      if (!isOpenRoute(history.location.pathname))
        history.replace("/login")
    }
  };

  useEffect(() => {
    initial();
  }, [initial]);

  return (
    <CognitoOTPAuthContext.Provider
      value={{
        ...state,
        method: 'cognito',
        user: {
          displayName: state?.user?.given_name,
          email: state?.user?.email,
        },
        initiateLogin,
        login,
        logout,
      }}
    >
      {children}
    </CognitoOTPAuthContext.Provider>
  );
};

export default CognitoOTPAuthProvider;